import React, { useState, useEffect } from 'react';
import { 
  FileText, CheckCircle2, ShieldCheck, DollarSign, Sparkles, Download, 
  Copy, Check, AlertTriangle, ChevronRight, Layers, FileSpreadsheet, 
  Scale, BookOpen, Clock, Building2, UploadCloud, RefreshCw, X, 
  ExternalLink, ArrowRight, Award, AlertCircle, FileCheck, Eye, Globe,
  Calendar, CheckSquare, Cloud, Loader2, Folder
} from 'lucide-react';
import { DEFAULT_16_RFX_REFERENCE_DOCUMENTS, RFXReferenceDocument } from '../data/rfxReferenceDocuments';
import { MultiVolumeProposalDraft, MarketCostAnalysis, RequirementMatrixRow } from '../types/proposalTypes';
import { comprehensiveProposalService, generateLocalMarketAnalysis } from '../services/comprehensiveProposalService';
import { 
  hasActiveWorkspaceConnection,
  connectWorkspace,
  exportProposalToGoogleDoc,
  exportVolumeToGoogleDoc,
  exportRequirementsToGoogleSheet,
  exportLaborRatesToGoogleSheet,
  syncProposalDeadlinesToGoogleCalendar,
  syncProposalChecklistToGoogleTasks
} from '../services/googleAuth';

interface ComprehensiveProposalStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  solicitation: any;
  vendorProfile?: any;
  onSavedToDatabase?: (draft: MultiVolumeProposalDraft) => void;
}

export const ComprehensiveProposalStudioModal: React.FC<ComprehensiveProposalStudioModalProps> = ({
  isOpen,
  onClose,
  solicitation,
  vendorProfile,
  onSavedToDatabase
}) => {
  // Active Tab States
  const [activeVolumeTab, setActiveVolumeTab] = useState<'vol1' | 'vol2' | 'vol3' | 'vol4' | 'vol5' | 'vol6' | 'docs' | 'market'>('vol1');
  
  // Document selection & uploads
  const [sourcingDocs, setSourcingDocs] = useState<RFXReferenceDocument[]>(DEFAULT_16_RFX_REFERENCE_DOCUMENTS);
  const [selectedDocId, setSelectedDocId] = useState<string>('doc_05');
  const [customUploadedFiles, setCustomUploadedFiles] = useState<{ name: string; size: string; type: string }[]>([]);
  const [docCategoryFilter, setDocCategoryFilter] = useState<string>('all');

  // Proposal Generation & Market Parameters
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [discountPct, setDiscountPct] = useState<number>(5);
  const [customInstructions, setCustomInstructions] = useState<string>('');
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // Google Workspace Sync States
  const [isWorkspaceConnected, setIsWorkspaceConnected] = useState<boolean>(hasActiveWorkspaceConnection());
  const [workspaceLoading, setWorkspaceLoading] = useState<boolean>(false);
  const [workspaceSyncResult, setWorkspaceSyncResult] = useState<{
    type: 'doc' | 'sheet' | 'calendar' | 'task';
    title: string;
    link?: string;
    message: string;
  } | null>(null);
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState<boolean>(false);

  // Proposal Data State
  const [proposalData, setProposalData] = useState<MultiVolumeProposalDraft | null>(null);

  // Initial Load / Proposal Synthesis
  useEffect(() => {
    if (isOpen && solicitation) {
      // Check if we already have a draft or generate fresh one
      loadOrGenerateProposal();
      setIsWorkspaceConnected(hasActiveWorkspaceConnection());
    }
  }, [isOpen, solicitation]);

  const loadOrGenerateProposal = async () => {
    setIsGenerating(true);
    try {
      const draft = await comprehensiveProposalService.generateComprehensiveProposal({
        solicitation: {
          solicitationId: solicitation?.solicitationId || 'RFX-2026-DEFAULT',
          title: solicitation?.title || 'State Procurement Modernization Project',
          agency: solicitation?.agency || 'State Department of Administration',
          state: solicitation?.state || 'TX',
          pricing: solicitation?.pricing || 350000,
          description: solicitation?.description || ''
        },
        vendorProfile: vendorProfile || {
          companyName: 'Quantum AI Solutions Inc.',
          uei: 'SAM-UEI-9847291',
          cageCode: '8V3K1'
        },
        selectedDocuments: sourcingDocs.filter(d => d.defaultSelected),
        marketParameters: {
          competitiveDiscountPct: discountPct
        },
        customInstructions
      });
      setProposalData(draft);
    } catch (err) {
      console.error('Error generating comprehensive proposal:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  const activeDoc = sourcingDocs.find(d => d.id === selectedDocId) || sourcingDocs[0];

  const handleToggleDoc = (id: string) => {
    setSourcingDocs(prev => prev.map(d => d.id === id ? { ...d, defaultSelected: !d.defaultSelected } : d));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files).map(f => ({
      name: f.name,
      size: `${(f.size / (1024 * 1024)).toFixed(2)} MB`,
      type: f.name.split('.').pop()?.toUpperCase() || 'DOC'
    }));
    setCustomUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(label);
    setTimeout(() => setCopiedSection(null), 2500);
  };

  // Helper to ensure workspace connection before API actions
  const ensureWorkspaceConnected = async (): Promise<boolean> => {
    if (hasActiveWorkspaceConnection()) {
      setIsWorkspaceConnected(true);
      return true;
    }
    try {
      setWorkspaceLoading(true);
      const token = await connectWorkspace(vendorProfile?.id);
      if (!token) {
        return false;
      }
      setIsWorkspaceConnected(true);
      return true;
    } catch (error: any) {
      if (
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request' ||
        (typeof error?.message === 'string' && error.message.includes('popup-closed-by-user'))
      ) {
        console.warn('Workspace authentication popup dismissed by user.');
        return false;
      }
      console.error('Failed to connect workspace:', error);
      setWorkspaceSyncResult({
        type: 'doc',
        title: 'Connection Error',
        message: 'Could not connect to Google Workspace. Please permit the Google sign-in window.'
      });
      return false;
    } finally {
      setWorkspaceLoading(false);
    }
  };

  // =========================================================================
  // Google Workspace Export Actions
  // =========================================================================

  const handleExportToGoogleDocAllVolumes = async () => {
    if (!proposalData) return;
    const ok = await ensureWorkspaceConnected();
    if (!ok) return;

    setWorkspaceLoading(true);
    setShowWorkspaceDropdown(false);
    try {
      const res = await exportProposalToGoogleDoc(proposalData, {
        companyName: vendorProfile?.companyName,
        id: vendorProfile?.id,
        uei: vendorProfile?.samUei || vendorProfile?.uei
      });
      setWorkspaceSyncResult({
        type: 'doc',
        title: res.title,
        link: res.webViewLink,
        message: `Official 16-document proposal successfully generated and saved to Drive under folder: ProcureFlow - ${vendorProfile?.companyName || 'Enterprise'}`
      });
    } catch (err: any) {
      console.error('Error exporting proposal to Google Docs:', err);
      setWorkspaceSyncResult({
        type: 'doc',
        title: 'Export Failed',
        message: err.message || 'Failed to export document to Google Docs.'
      });
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleExportActiveVolumeToGoogleDoc = async (volTitle: string, volContent: string) => {
    if (!proposalData) return;
    const ok = await ensureWorkspaceConnected();
    if (!ok) return;

    setWorkspaceLoading(true);
    try {
      const res = await exportVolumeToGoogleDoc(volTitle, volContent, proposalData, {
        companyName: vendorProfile?.companyName,
        id: vendorProfile?.id
      });
      setWorkspaceSyncResult({
        type: 'doc',
        title: res.title,
        link: res.webViewLink,
        message: `${volTitle} successfully exported to Google Docs in your entity folder!`
      });
    } catch (err: any) {
      console.error('Error exporting volume to Google Docs:', err);
      setWorkspaceSyncResult({
        type: 'doc',
        title: 'Volume Export Failed',
        message: err.message || 'Failed to export volume.'
      });
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleExportRequirementsToGoogleSheets = async () => {
    if (!proposalData) return;
    const ok = await ensureWorkspaceConnected();
    if (!ok) return;

    setWorkspaceLoading(true);
    setShowWorkspaceDropdown(false);
    try {
      const res = await exportRequirementsToGoogleSheet(proposalData, {
        companyName: vendorProfile?.companyName,
        id: vendorProfile?.id
      });
      setWorkspaceSyncResult({
        type: 'sheet',
        title: res.title,
        link: res.webViewLink,
        message: `Attachment H Requirements Matrix (${proposalData.volume3RequirementsMatrix?.length || 0} reqs) exported to Google Sheets.`
      });
    } catch (err: any) {
      console.error('Error exporting matrix to Sheets:', err);
      setWorkspaceSyncResult({
        type: 'sheet',
        title: 'Export Failed',
        message: err.message || 'Failed to export to Google Sheets.'
      });
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleExportLaborRatesToGoogleSheets = async () => {
    if (!proposalData) return;
    const ok = await ensureWorkspaceConnected();
    if (!ok) return;

    setWorkspaceLoading(true);
    setShowWorkspaceDropdown(false);
    try {
      const res = await exportLaborRatesToGoogleSheet(proposalData, {
        companyName: vendorProfile?.companyName,
        id: vendorProfile?.id
      });
      setWorkspaceSyncResult({
        type: 'sheet',
        title: res.title,
        link: res.webViewLink,
        message: `Attachment I Cost Proposal & Price-to-Win Labor Schedule exported to Google Sheets.`
      });
    } catch (err: any) {
      console.error('Error exporting cost to Sheets:', err);
      setWorkspaceSyncResult({
        type: 'sheet',
        title: 'Export Failed',
        message: err.message || 'Failed to export pricing model.'
      });
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleSyncDeadlinesToCalendar = async () => {
    const ok = await ensureWorkspaceConnected();
    if (!ok) return;

    setWorkspaceLoading(true);
    setShowWorkspaceDropdown(false);
    try {
      const events = await syncProposalDeadlinesToGoogleCalendar(solicitation, proposalData);
      setWorkspaceSyncResult({
        type: 'calendar',
        title: 'Calendar Milestones Synced',
        link: events[0]?.htmlLink || 'https://calendar.google.com',
        message: `Synced ${events.length} proposal deadline and Red Team review milestones directly to Google Calendar.`
      });
    } catch (err: any) {
      console.error('Error syncing to Calendar:', err);
      setWorkspaceSyncResult({
        type: 'calendar',
        title: 'Calendar Sync Failed',
        message: err.message || 'Could not create calendar events.'
      });
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleSyncChecklistToTasks = async () => {
    if (!proposalData) return;
    const ok = await ensureWorkspaceConnected();
    if (!ok) return;

    setWorkspaceLoading(true);
    setShowWorkspaceDropdown(false);
    try {
      const tasks = await syncProposalChecklistToGoogleTasks(proposalData);
      setWorkspaceSyncResult({
        type: 'task',
        title: 'Checklist Pushed to Google Tasks',
        link: 'https://tasks.google.com',
        message: `Added ${tasks.length} submission compliance steps to your Google Tasks list.`
      });
    } catch (err: any) {
      console.error('Error syncing to Google Tasks:', err);
      setWorkspaceSyncResult({
        type: 'task',
        title: 'Tasks Sync Failed',
        message: err.message || 'Could not push tasks.'
      });
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleExportFullProposal = () => {
    if (!proposalData) return;
    const fullMarkdown = `# COMPREHENSIVE PROPOSAL PACKAGE
## Solicitation: ${proposalData.solicitationTitle} (${proposalData.solicitationId})
## Target Agency: ${proposalData.agency} - ${proposalData.state}
## Submitting Vendor: ${proposalData.vendorName}
## Overall Compliance Score: ${proposalData.overallComplianceScore}% | Status: ${proposalData.marketCompetitivenessRating}

---

# VOLUME I: ADMINISTRATIVE TRANSMITTAL & EXECUTIVE SUMMARY
${proposalData.volume1ExecutiveSummary.transmittalLetter}

${proposalData.volume1ExecutiveSummary.executiveSummary}

${proposalData.volume1ExecutiveSummary.contractorProfile}

${proposalData.volume1ExecutiveSummary.keyPersonnelSummary}

${proposalData.volume1ExecutiveSummary.subcontractingHubPlan}

---

# VOLUME II: TECHNICAL APPROACH & STATEMENT OF WORK EXECUTION
${proposalData.volume2TechnicalApproach.understandingOfNeed}

${proposalData.volume2TechnicalApproach.technicalMethodologyAndArchitecture}

${proposalData.volume2TechnicalApproach.workBreakdownStructureAndSprintPlan}

${proposalData.volume2TechnicalApproach.qualityAssuranceAndSLACommitment}

${proposalData.volume2TechnicalApproach.securityComplianceNistSummary}

${proposalData.volume2TechnicalApproach.riskMitigationPlan}

---

# VOLUME III: REQUIREMENTS RESPONSE TABLE (ATTACHMENT H)
| Req ID | Category | Requirement | Compliance Level | Response Narrative | Cross-Reference |
|---|---|---|---|---|---|
${proposalData.volume3RequirementsMatrix.map(r => `| ${r.reqId} | ${r.category} | ${r.requirementText} | ${r.complianceLevel} | ${r.technicalResponseNarrative} | ${r.crossReference} |`).join('\n')}

---

# VOLUME IV: COST PROPOSAL & FINANCIAL MODEL (ATTACHMENT I)
**Proposed Total Contract Value**: $${proposalData.volume4CostProposal.finalProposedPrice.toLocaleString()}
**Price-to-Win Score**: ${proposalData.volume4CostProposal.priceToWinScore}/100

### Labor Category Breakdown:
| Labor Category / Role | Market Median Rate | Proposed Hourly Rate | Proposed Hours | Extended Price | Justification |
|---|---|---|---|---|---|
${proposalData.volume4CostProposal.laborRates.map(l => `| ${l.role} (${l.laborCategory}) | $${l.marketMedianRate}/hr | $${l.proposedHourlyRate}/hr | ${l.proposedHours} hrs | $${l.extendedCost.toLocaleString()} | ${l.justification} |`).join('\n')}

---

# VOLUME V: LEGAL CERTIFICATIONS & REDLINES (ATTACHMENTS A1, G, J, K)
${proposalData.volume5LegalAndForms.nonCollusionAffidavitText}

${proposalData.volume5LegalAndForms.offerorCertificationsSummary}

---

# VOLUME VI: SUBMISSION PACKAGING PROTOCOL (ATTACHMENT L)
${proposalData.volume6SubmissionChecklist.map(s => `- **${s.step}**: ${s.requirement} [Status: ${s.status}] - ${s.notes}`).join('\n')}
`;

    const blob = new Blob([fullMarkdown], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Proposal_${proposalData.solicitationId}_Compliant_Package.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportRequirementsCSV = () => {
    if (!proposalData) return;
    const headers = ['Requirement ID', 'Category', 'Requirement Text', 'Source Document', 'Compliance Level', 'Technical Response Narrative', 'Cross Reference'];
    const rows = proposalData.volume3RequirementsMatrix.map(r => [
      `"${r.reqId}"`,
      `"${r.category}"`,
      `"${r.requirementText.replace(/"/g, '""')}"`,
      `"${r.sourceDocument}"`,
      `"${r.complianceLevel}"`,
      `"${r.technicalResponseNarrative.replace(/"/g, '""')}"`,
      `"${r.crossReference}"`
    ]);
    const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Attachment_H_Requirements_Response_${proposalData.solicitationId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveToRFXDatabase = () => {
    if (!proposalData) return;
    if (onSavedToDatabase) {
      onSavedToDatabase(proposalData);
    }
    setSaveStatus('Proposal package successfully synced to RFX Database & Bid Vault!');
    setTimeout(() => setSaveStatus(null), 3500);
  };

  const filteredDocs = sourcingDocs.filter(d => {
    if (docCategoryFilter === 'all') return true;
    if (docCategoryFilter === 'mandatory') return d.mandatory;
    if (docCategoryFilter === 'forms') return d.category === 'certification' || d.category === 'vendor_info';
    if (docCategoryFilter === 'pricing') return d.category === 'pricing' || d.category === 'matrix';
    if (docCategoryFilter === 'legal') return d.category === 'legal_terms' || d.category === 'contract';
    return true;
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white w-full max-w-7xl rounded-2xl shadow-2xl border border-gray-100 flex flex-col max-h-[94vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* HEADER BAR */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-indigo-800/30">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-500/20 border border-indigo-400/30 rounded-xl">
              <Sparkles className="w-5 h-5 text-indigo-300 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-indigo-500/30 text-indigo-200 rounded font-bold">
                  Sourcing Ingestion & Proposal Studio
                </span>
                <span className="text-[10px] font-mono px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-bold flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  16 Documents Analyzed
                </span>
                {vendorProfile?.companyName && (
                  <span className="text-[10px] font-mono px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded font-bold flex items-center gap-1 border border-blue-400/30" title="Synchronized with active business entity">
                    <Building2 className="w-3 h-3 text-blue-400" />
                    {vendorProfile.companyName}
                  </span>
                )}
                {vendorProfile?.websiteIntelligence && (
                  <span className="text-[10px] font-mono px-2 py-0.5 bg-indigo-500/20 text-indigo-300 rounded font-bold flex items-center gap-1 border border-indigo-400/30" title="Proposal drafted using company website intelligence and past performance">
                    <Globe className="w-3 h-3 text-indigo-400" />
                    Dossier Active
                  </span>
                )}
              </div>
              <h2 className="text-base font-bold text-white mt-0.5 line-clamp-1">
                {solicitation?.title || 'Enterprise Sourcing Opportunity'}
              </h2>
              <p className="text-xs text-indigo-200/70 font-mono">
                {solicitation?.agency || 'State Procurement Bureau'} &bull; Solicitation ID: {solicitation?.solicitationId || 'RFP-2026'} &bull; State: {solicitation?.state || 'TX'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Google Workspace Direct Action Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowWorkspaceDropdown(!showWorkspaceDropdown)}
                disabled={workspaceLoading || !proposalData}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-blue-400/40 shadow-xs"
                title="Synchronize proposal package directly with Google Docs, Sheets, Calendar, and Drive"
              >
                {workspaceLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Cloud className="w-3.5 h-3.5 text-blue-200" />
                )}
                <span>Google Workspace</span>
                <span className={`w-2 h-2 rounded-full ${isWorkspaceConnected ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              </button>

              {showWorkspaceDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white text-gray-800 rounded-xl shadow-2xl border border-gray-200 py-2 z-50 animate-in fade-in zoom-in-95">
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/70">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider font-mono">Google Sync</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${isWorkspaceConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {isWorkspaceConnected ? 'Connected' : 'Sign-in Ready'}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                      Entity: <span className="font-semibold text-gray-800">{vendorProfile?.companyName || 'Active Entity'}</span>
                    </p>
                  </div>

                  <div className="p-1 space-y-0.5">
                    <button
                      onClick={handleExportToGoogleDocAllVolumes}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      <div>
                        <div className="font-semibold">Export All 16 Docs to Google Docs</div>
                        <div className="text-[10px] text-gray-400">Formatted multi-volume proposal package</div>
                      </div>
                    </button>

                    <button
                      onClick={handleExportRequirementsToGoogleSheets}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                      <div>
                        <div className="font-semibold">Export Att. H to Google Sheets</div>
                        <div className="text-[10px] text-gray-400">45+ Requirements Response Matrix</div>
                      </div>
                    </button>

                    <button
                      onClick={handleExportLaborRatesToGoogleSheets}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <DollarSign className="w-4 h-4 text-indigo-600" />
                      <div>
                        <div className="font-semibold">Export Att. I Pricing to Sheets</div>
                        <div className="text-[10px] text-gray-400">Labor Rates & PTW Cost Breakdown</div>
                      </div>
                    </button>

                    <button
                      onClick={handleSyncDeadlinesToCalendar}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <Calendar className="w-4 h-4 text-purple-600" />
                      <div>
                        <div className="font-semibold">Sync Deadlines to Calendar</div>
                        <div className="text-[10px] text-gray-400">RFP submission & Red Team gates</div>
                      </div>
                    </button>

                    <button
                      onClick={handleSyncChecklistToTasks}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-amber-50 hover:text-amber-700 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <CheckSquare className="w-4 h-4 text-amber-600" />
                      <div>
                        <div className="font-semibold">Push Checklist to Google Tasks</div>
                        <div className="text-[10px] text-gray-400">16 compliance packaging steps</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={loadOrGenerateProposal}
              disabled={isGenerating}
              className="px-3 py-1.5 bg-indigo-600/80 hover:bg-indigo-600 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-indigo-400/30"
              title="Re-run AI Multi-Document Synthesis"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isGenerating ? 'animate-spin' : ''}`} />
              <span>{isGenerating ? 'Synthesizing...' : 'Regenerate Draft'}</span>
            </button>
            <button
              onClick={handleExportFullProposal}
              disabled={!proposalData}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-white/10"
              title="Download Complete Proposal as Markdown Document"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export Package</span>
            </button>
            <button
              onClick={handleSaveToRFXDatabase}
              disabled={!proposalData}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <FileCheck className="w-3.5 h-3.5" />
              <span>Save & Sync to RFX Vault</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* GOOGLE WORKSPACE SYNC SUCCESS BANNER */}
        {workspaceSyncResult && (
          <div className="px-6 py-2.5 bg-blue-50 border-b border-blue-200 text-blue-900 text-xs font-medium flex items-center justify-between animate-in fade-in">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
              <div>
                <span className="font-bold">{workspaceSyncResult.title}:</span> {workspaceSyncResult.message}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {workspaceSyncResult.link && (
                <a
                  href={workspaceSyncResult.link}
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow-2xs transition-all"
                >
                  <span>Open Item</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button 
                onClick={() => setWorkspaceSyncResult(null)} 
                className="text-blue-700 hover:text-blue-900 font-bold px-1.5"
              >
                &times;
              </button>
            </div>
          </div>
        )}

        {/* STATUS BANNER */}
        {saveStatus && (
          <div className="px-6 py-2 bg-emerald-50 border-b border-emerald-200 text-emerald-800 text-xs font-medium flex items-center justify-between animate-in fade-in">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              {saveStatus}
            </span>
            <button onClick={() => setSaveStatus(null)} className="text-emerald-700 hover:text-emerald-900 font-bold">&times;</button>
          </div>
        )}

        {/* TOP METRICS & WORKFLOW BAR */}
        <div className="px-6 py-2.5 bg-gray-50 border-b border-gray-200/80 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-gray-500">Compliance Score:</span>
              <span className="font-bold text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-md font-mono">
                {proposalData?.overallComplianceScore || 99.4}%
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-gray-500">Price-to-Win Rating:</span>
              <span className="font-bold text-indigo-700 bg-indigo-100/70 px-2 py-0.5 rounded-md font-mono">
                {proposalData?.marketCompetitivenessRating || 'Superior Tier-1 (Optimal PTW)'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-gray-500">Proposed Total (Att. I):</span>
              <span className="font-bold text-gray-900 bg-gray-200/70 px-2 py-0.5 rounded-md font-mono">
                ${proposalData?.volume4CostProposal.finalProposedPrice.toLocaleString() || '205,500'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-[11px] text-gray-500 font-medium">Strategic Discount:</label>
            <select
              value={discountPct}
              onChange={(e) => setDiscountPct(Number(e.target.value))}
              className="px-2 py-1 bg-white border border-gray-200 rounded-md text-xs font-semibold text-gray-700 focus:outline-none focus:border-indigo-500"
            >
              <option value={0}>0% (Standard Benchmark)</option>
              <option value={5}>5% (Recommended PTW)</option>
              <option value={10}>10% (Aggressive Public Sector)</option>
              <option value={15}>15% (Maximum Price-to-Win)</option>
            </select>
          </div>
        </div>

        {/* NAVIGATION TABS */}
        <div className="px-6 bg-white border-b border-gray-200 flex items-center gap-1 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveVolumeTab('vol1')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'vol1'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>Vol I: Admin & Exec Summary</span>
          </button>

          <button
            onClick={() => setActiveVolumeTab('vol2')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'vol2'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Vol II: Technical SOW Approach</span>
          </button>

          <button
            onClick={() => setActiveVolumeTab('vol3')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'vol3'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
            <span>Vol III: Att. H Requirements Matrix</span>
            <span className="px-1.5 py-0.2 bg-emerald-100 text-emerald-800 text-[10px] rounded-full font-mono font-bold">45+ Reqs</span>
          </button>

          <button
            onClick={() => setActiveVolumeTab('vol4')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'vol4'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5 text-indigo-600" />
            <span>Vol IV: Att. I Cost Proposal</span>
            <span className="px-1.5 py-0.2 bg-indigo-100 text-indigo-800 text-[10px] rounded-full font-mono font-bold">Market Research</span>
          </button>

          <button
            onClick={() => setActiveVolumeTab('vol5')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'vol5'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <Scale className="w-3.5 h-3.5 text-amber-600" />
            <span>Vol V: Legal & Redlines (A1, G, J, K)</span>
          </button>

          <button
            onClick={() => setActiveVolumeTab('vol6')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'vol6'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5 text-sky-600" />
            <span>Vol VI: Att. L Submission Guide</span>
          </button>

          <button
            onClick={() => setActiveVolumeTab('docs')}
            className={`py-3 px-3.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeVolumeTab === 'docs'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5 text-purple-600" />
            <span>16 Sourcing Documents Hub</span>
          </button>
        </div>

        {/* MAIN BODY VIEW AREA */}
        <div className="flex-1 p-6 overflow-y-auto bg-slate-50/60">
          
          {/* VOLUME 1: ADMINISTRATIVE TRANSMITTAL & EXECUTIVE SUMMARY */}
          {activeVolumeTab === 'vol1' && proposalData && (
            <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Volume I: Administrative Transmittal & Executive Overview</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Compliant response incorporating 01_RFP_Overview.docx, 04_Attachment_A2, and 08_Attachment_D1.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExportActiveVolumeToGoogleDoc('Volume I: Admin Transmittal & Exec Summary', proposalData.volume1ExecutiveSummary.transmittalLetter + '\n\n' + proposalData.volume1ExecutiveSummary.executiveSummary + '\n\n' + proposalData.volume1ExecutiveSummary.contractorProfile)}
                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-blue-200"
                    title="Export Volume I directly to Google Docs"
                  >
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    <span>Sync to Google Doc</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(proposalData.volume1ExecutiveSummary.transmittalLetter + '\n\n' + proposalData.volume1ExecutiveSummary.executiveSummary, 'vol1')}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    {copiedSection === 'vol1' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedSection === 'vol1' ? 'Copied' : 'Copy Volume I'}</span>
                  </button>
                </div>
              </div>

              {/* Transmittal Letter Card */}
              <div className="bg-white p-6 rounded-xl border border-gray-200/80 shadow-2xs space-y-4">
                <div className="flex items-center justify-between border-b border-gray-150 pb-3">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded">
                    Official Transmittal Letter
                  </span>
                  <span className="text-xs text-gray-400 font-mono">180-Day Binding Guarantee</span>
                </div>
                <div className="prose prose-sm text-gray-800 text-xs leading-relaxed whitespace-pre-line font-serif bg-gray-50/50 p-5 rounded-lg border border-gray-100">
                  {proposalData.volume1ExecutiveSummary.transmittalLetter}
                </div>
              </div>

              {/* Executive Summary Card */}
              <div className="bg-white p-6 rounded-xl border border-gray-200/80 shadow-2xs space-y-4">
                <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-gray-700 border-b border-gray-150 pb-2">
                  Executive Summary & Value Drivers
                </h4>
                <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-line space-y-2">
                  {proposalData.volume1ExecutiveSummary.executiveSummary}
                </div>
              </div>

              {/* Contractor Credentials & HUB Allocation */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-2xs space-y-2">
                  <h5 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-indigo-600" />
                    Contractor Profile & Certifications
                  </h5>
                  <div className="text-xs text-gray-600 whitespace-pre-line leading-relaxed">
                    {proposalData.volume1ExecutiveSummary.contractorProfile}
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-2xs space-y-2">
                  <h5 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                    <Award className="w-4 h-4 text-amber-600" />
                    Subcontracting & HUB/MWBE Commitment
                  </h5>
                  <div className="text-xs text-gray-600 whitespace-pre-line leading-relaxed">
                    {proposalData.volume1ExecutiveSummary.subcontractingHubPlan}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VOLUME 2: TECHNICAL APPROACH & SOW EXECUTION */}
          {activeVolumeTab === 'vol2' && proposalData && (
            <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Volume II: Technical Approach & Statement of Work Execution</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Fulfills 05_Attachment_B_-_Scope_of_Work.docx and aligns with 06_Attachment_C Evaluation Plan (40% Weight).</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExportActiveVolumeToGoogleDoc('Volume II: Technical Approach & SOW', proposalData.volume2TechnicalApproach.technicalMethodologyAndArchitecture + '\n\n' + proposalData.volume2TechnicalApproach.workBreakdownStructureAndSprintPlan)}
                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-blue-200"
                    title="Export Volume II directly to a Google Doc in your entity folder"
                  >
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    <span>Sync to Google Doc</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(proposalData.volume2TechnicalApproach, null, 2), 'vol2')}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    {copiedSection === 'vol2' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedSection === 'vol2' ? 'Copied' : 'Copy Volume II'}</span>
                  </button>
                </div>
              </div>

              {/* Technical Methodology & Cloud Architecture */}
              <div className="bg-white p-6 rounded-xl border border-gray-200/80 shadow-2xs space-y-3">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                  Section 2.0 - Solution Architecture & Microservices
                </span>
                <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                  {proposalData.volume2TechnicalApproach.technicalMethodologyAndArchitecture}
                </div>
              </div>

              {/* Phased Sprint Schedule & Work Breakdown */}
              <div className="bg-white p-6 rounded-xl border border-gray-200/80 shadow-2xs space-y-3">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                  Section 3.0 - 180-Day Agile Sprint Execution Plan
                </span>
                <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                  {proposalData.volume2TechnicalApproach.workBreakdownStructureAndSprintPlan}
                </div>
              </div>

              {/* Quality, SLAs, NIST Security, and Risk Management */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-2xs space-y-2">
                  <h5 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    NIST SP 800-53 Security Controls
                  </h5>
                  <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-line">
                    {proposalData.volume2TechnicalApproach.securityComplianceNistSummary}
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-2xs space-y-2">
                  <h5 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    SLAs & 99.99% Availability Commitment
                  </h5>
                  <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-line">
                    {proposalData.volume2TechnicalApproach.qualityAssuranceAndSLACommitment}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* VOLUME 3: REQUIREMENTS RESPONSE TABLE (ATTACHMENT H) */}
          {activeVolumeTab === 'vol3' && proposalData && (
            <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <span>Attachment H: Requirements Response Table</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] rounded font-bold font-mono">100% Fully Compliant</span>
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Exhaustive requirement mapping across functional, technical, SLA, and security requirements.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportRequirementsToGoogleSheets}
                    className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-emerald-200"
                    title="Export Attachment H matrix directly to a new Google Sheet"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Sync to Google Sheets</span>
                  </button>
                  <button
                    onClick={handleExportRequirementsCSV}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-gray-200"
                  >
                    <Download className="w-3.5 h-3.5 text-gray-600" />
                    <span>Download CSV</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(proposalData.volume3RequirementsMatrix, null, 2), 'vol3')}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    {copiedSection === 'vol3' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedSection === 'vol3' ? 'Copied' : 'Copy Matrix'}</span>
                  </button>
                </div>
              </div>

              {/* Requirement Matrix Spreadsheet Table */}
              <div className="bg-white rounded-xl border border-gray-200/80 shadow-2xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100/80 border-b border-gray-200 text-gray-700 font-mono text-[11px]">
                        <th className="py-2.5 px-3 font-bold w-20">Req ID</th>
                        <th className="py-2.5 px-3 font-bold w-32">Category</th>
                        <th className="py-2.5 px-3 font-bold">Requirement Specification</th>
                        <th className="py-2.5 px-3 font-bold w-36">Compliance Level</th>
                        <th className="py-2.5 px-3 font-bold">Technical Response Narrative</th>
                        <th className="py-2.5 px-3 font-bold w-28">Cross-Ref</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150">
                      {proposalData.volume3RequirementsMatrix.map((row, idx) => (
                        <tr key={idx} className="hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2.5 px-3 font-mono font-bold text-indigo-700">{row.reqId}</td>
                          <td className="py-2.5 px-3 font-semibold text-gray-700">{row.category}</td>
                          <td className="py-2.5 px-3 text-gray-800 font-medium">{row.requirementText}</td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold font-mono ${
                              row.complianceLevel.includes('Exceeds') 
                                ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                                : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                            }`}>
                              {row.complianceLevel}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-gray-600 leading-relaxed">{row.technicalResponseNarrative}</td>
                          <td className="py-2.5 px-3 font-mono text-[10px] text-gray-400">{row.crossReference}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* VOLUME 4: COST PROPOSAL & FINANCIAL MODEL (ATTACHMENT I) */}
          {activeVolumeTab === 'vol4' && proposalData && (
            <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <span>Attachment I: Cost Proposal Form & Market Rate Analysis</span>
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] rounded font-bold font-mono">GSA MAS / BLS Benchmarked</span>
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Exhaustively researched labor categories, hours, milestones, and price-to-win model.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportLaborRatesToGoogleSheets}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-indigo-200"
                    title="Export labor pricing matrix directly to Google Sheets"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Sync Pricing to Sheets</span>
                  </button>
                  <span className="text-xs font-semibold text-gray-600">Proposed Total:</span>
                  <span className="text-base font-extrabold text-emerald-700 font-mono bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200">
                    ${proposalData.volume4CostProposal.finalProposedPrice.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Market Research Benchmark Card */}
              <div className="bg-indigo-950 text-white p-5 rounded-xl border border-indigo-800 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-indigo-300 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-300" />
                    Market Analysis Methodology & Regional Cost Index
                  </span>
                  <span className="text-xs font-mono text-indigo-200">
                    Regional Multiplier ({proposalData.volume4CostProposal.targetState}): {proposalData.volume4CostProposal.regionalCostIndex.toFixed(2)}x
                  </span>
                </div>
                <p className="text-xs text-indigo-100 leading-relaxed">
                  {proposalData.volume4CostProposal.marketRateBenchmarkSummary}
                </p>
                <div className="p-3 bg-white/10 rounded-lg border border-white/10 text-xs text-indigo-200 flex items-center justify-between">
                  <span>Price-to-Win Assessment: {proposalData.volume4CostProposal.priceToWinAssessment}</span>
                  <span className="font-mono font-bold text-emerald-300">PTW Score: {proposalData.volume4CostProposal.priceToWinScore}/100</span>
                </div>
              </div>

              {/* Labor Rates Breakdown Spreadsheet Table */}
              <div className="bg-white rounded-xl border border-gray-200/80 shadow-2xs overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 font-bold text-xs text-gray-800">
                  Labor Category Rate Sheet & Hours Allocation
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100/80 border-b border-gray-200 text-gray-700 font-mono text-[11px]">
                        <th className="py-2.5 px-3 font-bold">Labor Category / Proposed Role</th>
                        <th className="py-2.5 px-3 font-bold text-right">Market Median</th>
                        <th className="py-2.5 px-3 font-bold text-right">Proposed Rate</th>
                        <th className="py-2.5 px-3 font-bold text-right">Allocated Hours</th>
                        <th className="py-2.5 px-3 font-bold text-right">Extended Price</th>
                        <th className="py-2.5 px-3 font-bold">Benchmark Source / Justification</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150">
                      {proposalData.volume4CostProposal.laborRates.map((item, idx) => (
                        <tr key={idx} className="hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2.5 px-3 font-semibold text-gray-900">
                            {item.role}
                            <span className="block text-[10px] text-gray-400 font-normal">{item.laborCategory}</span>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-gray-500">${item.marketMedianRate}/hr</td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-indigo-700">${item.proposedHourlyRate}/hr</td>
                          <td className="py-2.5 px-3 text-right font-mono text-gray-700">{item.proposedHours} hrs</td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-gray-900">${item.extendedCost.toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-gray-500 text-[11px] leading-relaxed">{item.justification}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-bold border-t border-gray-200">
                        <td colSpan={3} className="py-2.5 px-3 text-gray-700">Total Direct Labor (TDL)</td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700">
                          {proposalData.volume4CostProposal.laborRates.reduce((a, b) => a + b.proposedHours, 0)} hrs
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-emerald-700">
                          ${proposalData.volume4CostProposal.totalDirectLabor.toLocaleString()}
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-gray-400 font-mono">Exclusive of ODC & Margin</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Milestone Pricing Schedule */}
              <div className="bg-white rounded-xl border border-gray-200/80 shadow-2xs overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 font-bold text-xs text-gray-800">
                  Deliverables-Based Milestone Disbursement Schedule
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100/80 border-b border-gray-200 text-gray-700 font-mono text-[11px]">
                        <th className="py-2.5 px-3 font-bold w-16">ID</th>
                        <th className="py-2.5 px-3 font-bold">Project Milestone & Deliverables</th>
                        <th className="py-2.5 px-3 font-bold text-right">Timeline</th>
                        <th className="py-2.5 px-3 font-bold text-right">Milestone Price</th>
                        <th className="py-2.5 px-3 font-bold">Payment Trigger & Acceptance Sign-Off</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150">
                      {proposalData.volume4CostProposal.milestones.map((m, idx) => (
                        <tr key={idx} className="hover:bg-indigo-50/30 transition-colors">
                          <td className="py-2.5 px-3 font-mono font-bold text-indigo-700">{m.milestoneId}</td>
                          <td className="py-2.5 px-3">
                            <span className="font-bold text-gray-900 block">{m.name}</span>
                            <span className="text-[10px] text-gray-500 block mt-0.5">{m.deliverables.join(' • ')}</span>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-gray-600">{m.timelineWeeks} wks</td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-700">${m.totalMilestonePrice.toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-gray-600 text-[11px]">{m.paymentTrigger}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* VOLUME 5: LEGAL CERTIFICATIONS & REDLINES (ATTACHMENTS A1, G, J, K) */}
          {activeVolumeTab === 'vol5' && proposalData && (
            <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Volume V: Legal Affidavits, Master Agreement Redlines & Confidentiality</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Pre-populated forms for Attachment A1 (Non-Collusion), Attachment G (Offeror Certifications), Attachment J (Redlines), and Attachment K.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExportActiveVolumeToGoogleDoc('Volume V: Legal Affidavits & Redlines', proposalData.volume5LegalAndForms.nonCollusionAffidavitText + '\n\n' + proposalData.volume5LegalAndForms.offerorCertificationsSummary)}
                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-blue-200"
                    title="Export Volume V directly to Google Docs"
                  >
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    <span>Sync to Google Doc</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(proposalData.volume5LegalAndForms, null, 2), 'vol5')}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    {copiedSection === 'vol5' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedSection === 'vol5' ? 'Copied' : 'Copy Volume V'}</span>
                  </button>
                </div>
              </div>

              {/* Attachment A1 Non-Collusion Form Card */}
              <div className="bg-white p-6 rounded-xl border border-gray-200/80 shadow-2xs space-y-3">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                  Attachment A1 - OMES Form CP004 Non-Collusion Certification
                </span>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 font-mono text-xs text-gray-800 leading-relaxed whitespace-pre-line">
                  {proposalData.volume5LegalAndForms.nonCollusionAffidavitText}
                </div>
              </div>

              {/* Attachment J Proposed Modifications Redline Table */}
              <div className="bg-white rounded-xl border border-gray-200/80 shadow-2xs overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 font-bold text-xs text-gray-800">
                  Attachment J - Proposed Modifications to Sample Master Agreement
                </div>
                <div className="p-4 space-y-4">
                  {proposalData.volume5LegalAndForms.proposedModificationsToMasterAgreement.map((mod, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2 text-xs">
                      <div className="flex items-center justify-between font-bold text-gray-900">
                        <span>{mod.sectionNumber}</span>
                        <span className="text-[10px] font-mono text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Clarifying Redline</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div className="p-2.5 bg-red-50/70 border border-red-200/60 rounded text-red-900">
                          <span className="font-bold block text-[10px] uppercase font-mono text-red-700">Original Language:</span>
                          "{mod.originalLanguage}"
                        </div>
                        <div className="p-2.5 bg-emerald-50/70 border border-emerald-200/60 rounded text-emerald-900">
                          <span className="font-bold block text-[10px] uppercase font-mono text-emerald-700">Proposed Redline:</span>
                          "{mod.proposedRedline}"
                        </div>
                      </div>
                      <p className="text-gray-600 text-[11px]">
                        <strong>Business Justification:</strong> {mod.businessJustification}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attachment K Business Confidentiality */}
              <div className="bg-white p-5 rounded-xl border border-gray-200/80 shadow-2xs space-y-3">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                  Attachment K - Claim of Business Confidentiality & Trade Secret Redaction Log
                </span>
                <div className="divide-y divide-gray-150">
                  {proposalData.volume5LegalAndForms.confidentialityRedactionIndex.map((item, idx) => (
                    <div key={idx} className="py-2.5 text-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <span className="font-bold text-gray-800">{item.volumeAndSection}</span>: <span className="text-gray-600">{item.confidentialTopic}</span>
                      </div>
                      <span className="text-[10px] text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                        {item.tradeSecretLegalJustification}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* VOLUME 6: ATTACHMENT L SUBMISSION GUIDE & VERIFICATION */}
          {activeVolumeTab === 'vol6' && proposalData && (
            <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Volume VI: Supplier Bid Submission Guide & Final Verification Checklist</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Complies with 16_Attachment_L_-_Supplier_Bid_Submission_Guide.pdf packaging and upload protocols.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSyncChecklistToTasks}
                    className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-amber-200"
                    title="Push 16 compliance check steps into your Google Tasks"
                  >
                    <CheckSquare className="w-3.5 h-3.5 text-amber-600" />
                    <span>Push to Google Tasks</span>
                  </button>
                  <button
                    onClick={handleSyncDeadlinesToCalendar}
                    className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-purple-200"
                    title="Sync submission deadlines and review milestones to Google Calendar"
                  >
                    <Calendar className="w-3.5 h-3.5 text-purple-600" />
                    <span>Sync to Calendar</span>
                  </button>
                </div>
              </div>

              {/* Submission Checklist Items */}
              <div className="bg-white rounded-xl border border-gray-200/80 shadow-2xs overflow-hidden divide-y divide-gray-150">
                {proposalData.volume6SubmissionChecklist.map((step, idx) => (
                  <div key={idx} className="p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors">
                    <div className={`p-1.5 rounded-full mt-0.5 ${
                      step.status === 'Verified Ready' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <h5 className="text-xs font-bold text-gray-900">{step.step}</h5>
                        <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                          step.status === 'Verified Ready' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {step.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 font-medium">{step.requirement}</p>
                      <p className="text-[11px] text-gray-500">{step.notes}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Executive Action Plan */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 space-y-3">
                <h4 className="text-xs font-bold text-indigo-950 uppercase font-mono tracking-wider">
                  Final Pre-Flight Executive Action Items
                </h4>
                <div className="space-y-2 text-xs text-indigo-900">
                  {proposalData.executiveActionPlan.map((action, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <ChevronRight className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                      <span>{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 16 SOURCING DOCUMENTS HUB VIEW */}
          {activeVolumeTab === 'docs' && (
            <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Reference Sourcing Documents & Attachments Hub</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Inspect all 16 official sourcing documents, mandatory checkpoints, and upload custom RFX supplements.</p>
                </div>
                
                {/* Filter Pills */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {['all', 'mandatory', 'forms', 'pricing', 'legal'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setDocCategoryFilter(cat)}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-all capitalize cursor-pointer ${
                        docCategoryFilter === cat
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Document Explorer Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* Document List Column */}
                <div className="lg:col-span-5 space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {filteredDocs.map((doc) => (
                    <div
                      key={doc.id}
                      onClick={() => setSelectedDocId(doc.id)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start justify-between gap-2 ${
                        selectedDocId === doc.id
                          ? 'bg-indigo-50/80 border-indigo-300 shadow-xs ring-1 ring-indigo-200'
                          : 'bg-white border-gray-200/80 hover:bg-gray-50'
                      }`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] font-mono font-bold uppercase px-1.5 py-0.2 rounded ${
                            doc.fileFormat === 'xlsx' ? 'bg-emerald-100 text-emerald-800' :
                            doc.fileFormat === 'pdf' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {doc.fileFormat}
                          </span>
                          <p className="text-xs font-bold text-gray-900 line-clamp-1">{doc.description}</p>
                        </div>
                        <p className="text-[10px] text-gray-400 font-mono">{doc.filename}</p>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {doc.mandatory ? (
                          <span className="text-[9px] font-mono font-bold text-red-600 bg-red-50 px-1.5 py-0.2 rounded">Mandatory</span>
                        ) : (
                          <span className="text-[9px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.2 rounded">Optional</span>
                        )}
                        <input
                          type="checkbox"
                          checked={doc.defaultSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleDoc(doc.id);
                          }}
                          className="w-3.5 h-3.5 text-indigo-600 rounded cursor-pointer mt-1"
                        />
                      </div>
                    </div>
                  ))}

                  {/* Upload Custom Supplement Card */}
                  <div className="p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-center space-y-2">
                    <UploadCloud className="w-5 h-5 text-gray-400 mx-auto" />
                    <div className="text-xs text-gray-600 font-medium">Add Custom RFX Sourcing Files</div>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="custom-file-upload-studio"
                    />
                    <label
                      htmlFor="custom-file-upload-studio"
                      className="inline-block px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-lg cursor-pointer shadow-2xs"
                    >
                      Browse & Attach Files
                    </label>
                  </div>
                </div>

                {/* Document Detail & Requirements Inspector Column */}
                <div className="lg:col-span-7 bg-white p-6 rounded-xl border border-gray-200/80 shadow-2xs space-y-5">
                  <div className="flex items-center justify-between border-b border-gray-150 pb-3">
                    <div>
                      <span className="text-[10px] font-mono font-bold text-indigo-600 uppercase tracking-wider">{activeDoc.filename}</span>
                      <h4 className="text-sm font-bold text-gray-900">{activeDoc.description}</h4>
                    </div>
                    <span className="text-xs font-mono font-semibold text-gray-500 bg-gray-100 px-2.5 py-1 rounded">
                      Weight: {activeDoc.scoringWeight || 'Pass/Fail'}
                    </span>
                  </div>

                  <div>
                    <h5 className="text-[11px] font-mono uppercase tracking-wider text-gray-400 font-bold mb-1">
                      Key Requirements Summary
                    </h5>
                    <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 p-3.5 rounded-lg border border-gray-100">
                      {activeDoc.keyRequirementsSummary}
                    </p>
                  </div>

                  <div>
                    <h5 className="text-[11px] font-mono uppercase tracking-wider text-gray-400 font-bold mb-2">
                      Mandatory Compliance Checkpoints
                    </h5>
                    <div className="space-y-2">
                      {activeDoc.complianceCheckpoints.map((pt, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-gray-800">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{pt}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {activeDoc.extractedClauses && activeDoc.extractedClauses.length > 0 && (
                    <div>
                      <h5 className="text-[11px] font-mono uppercase tracking-wider text-gray-400 font-bold mb-2">
                        Extracted Binding Clauses & Protocols
                      </h5>
                      <div className="space-y-1.5">
                        {activeDoc.extractedClauses.map((clause, i) => (
                          <div key={i} className="p-2.5 bg-indigo-50/50 border border-indigo-100 rounded text-xs text-indigo-950 font-serif italic">
                            "{clause}"
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* FOOTER BAR */}
        <div className="px-6 py-3 bg-white border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Synthesized with Gemini Enterprise Drafting Model &bull; Fully Audit-Compliant</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleExportFullProposal}
              className="px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-lg transition-colors cursor-pointer"
            >
              Export Markdown (.md)
            </button>
            <button
              onClick={handleSaveToRFXDatabase}
              className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors cursor-pointer shadow-xs"
            >
              Confirm & Apply to Opportunity
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
